// src/pages/Domains.js
// « Mes domaines » : prouver la propriété d'un domaine avec un enregistrement DNS TXT.
// Un domaine vérifié débloque les scans programmés et les tests approfondis.
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, Plus, Trash2, CheckCircle, Clock, Copy, Loader2, RefreshCw } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';

function CopyField({ label, value }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copié`);
    } catch {
      toast.error('Copie impossible, sélectionnez le texte à la main');
    }
  };
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-zinc-500 text-[10px] font-mono uppercase w-14 shrink-0">{label}</span>
      <code className="flex-1 min-w-0 truncate bg-black border border-zinc-800 px-2 py-1 text-xs font-mono text-zinc-200" title={value}>{value}</code>
      <button onClick={copy} className="shrink-0 p-1.5 border border-zinc-800 hover:border-blue-500 text-zinc-400 hover:text-white" aria-label={`Copier ${label}`}>
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function Domains() {
  const { token } = useAuth();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/domains`, { headers: { Authorization: `Bearer ${token}` } });
      setDomains(res.data || []);
    } catch (err) {
      toast.error('Impossible de charger vos domaines');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!newDomain.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/domains`, { domain: newDomain.trim() }, { headers });
      setNewDomain('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setAdding(false);
    }
  };

  const verify = async (d) => {
    setVerifying(d.domain_id);
    try {
      await axios.post(`${API_URL}/api/domains/${d.domain_id}/verify`, {}, { headers });
      toast.success(`${d.domain} est vérifié`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Vérification impossible');
    } finally {
      setVerifying(null);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Retirer ${d.domain} de votre compte ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/domains/${d.domain_id}`, { headers });
      await load();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 max-w-4xl">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono uppercase tracking-tighter">Mes domaines</h1>
          <p className="text-zinc-500 text-sm mt-2 max-w-2xl">
            Prouvez que vous gérez un domaine pour débloquer les scans programmés et les tests approfondis.
            Il suffit d'ajouter un enregistrement DNS, chez votre hébergeur ou votre registrar.
          </p>
        </div>

        <form onSubmit={add} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="votre-domaine.fr"
              className="w-full bg-black border border-zinc-800 p-3 pl-10 text-white font-mono focus:border-blue-500 outline-none"
            />
          </div>
          <button type="submit" disabled={adding || !newDomain.includes('.')}
            className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-5 py-3 font-mono text-xs uppercase tracking-widest text-white">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter
          </button>
        </form>

        {loading ? (
          <p className="text-zinc-500 font-mono text-xs">Chargement…</p>
        ) : domains.length === 0 ? (
          <p className="text-zinc-500 text-sm">Aucun domaine pour l'instant.</p>
        ) : (
          <ul className="space-y-4">
            {domains.map((d) => (
              <li key={d.domain_id} className="bg-zinc-900 border border-zinc-800 p-5 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono text-white font-bold truncate">{d.domain}</span>
                    {d.verified ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-0.5 border border-green-500/40 text-green-400">
                        <CheckCircle className="w-3 h-3" /> Vérifié
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-0.5 border border-amber-500/40 text-amber-400">
                        <Clock className="w-3 h-3" /> En attente
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {!d.verified && (
                      <button onClick={() => verify(d)} disabled={verifying === d.domain_id}
                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-white">
                        {verifying === d.domain_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Vérifier
                      </button>
                    )}
                    <button onClick={() => remove(d)} className="p-2 border border-zinc-800 hover:border-red-500 text-zinc-500 hover:text-red-400" aria-label={`Retirer ${d.domain}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {!d.verified && (
                  <div className="border-t border-zinc-800 pt-4 space-y-3">
                    <p className="text-zinc-400 text-sm">
                      Dans la zone DNS de <span className="text-white">{d.domain}</span>, créez cet enregistrement, puis cliquez sur « Vérifier » :
                    </p>
                    <div className="space-y-2">
                      <CopyField label="Type" value="TXT" />
                      <CopyField label="Nom" value={d.dns_record.name} />
                      <CopyField label="Valeur" value={d.dns_record.value} />
                    </div>
                    <p className="text-zinc-600 text-xs">
                      Selon votre hébergeur, le champ « Nom » peut s'appeler « Hôte » ou « Sous-domaine » ; indiquez alors seulement <code className="text-zinc-400">_cyberguard</code>.
                      La prise en compte prend en général quelques minutes, parfois jusqu'à une heure. Laissez l'enregistrement en place : il est revérifié avant chaque scan programmé.
                    </p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </DashboardLayout>
  );
}
